import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { FilterSelection, Profile, ProfilesApiResponse } from '../../../tsm-app/models/tsm-app.model';
import {
	isWorkbookOutput,
	ProfileOption,
	ReportAppDescriptor,
	ReportResult,
} from '../../models/report-app.model';
import { ReportAppApiService } from '../../services/report-app-api.service';
import { TsmAppFormService } from '../../../tsm-app/services/tsm-app-form.service';
import { TmsLabelService } from '../../../../language/services/tms-label.service';
import {
	isFormExpiredError,
	closeFormQuietly,
	restoreFormSnapshot,
	retryOnFormExpired,
} from '../../../../shared/form-lifecycle';
import { TmsNotificationService } from '../../../../services/tms-notification.service';
import { ReportDownloadService } from '../../services/report-download.service';
import { carryProfileOptionsForward, computeActiveFilterCount, profilesToProfileOptions, projectFilterSelections } from '../../../filter-app/services/filter-panel-projection';
import { selectionsModified } from '../../../../shared/filters';
import {
	ApplyReportDescriptorUpdate,
	ApplyReportResult,
	BootstrapReportApp,
	ClearReportAppState,
	InitReportAppState,
	LoadReportAppProfiles,
	RebindReportForm,
	RefreshReportApp,
	RestoreReportAppSnapshot,
	RunReport,
	SetReportFormat,
	ToggleReportFilterPanel,
} from '../actions/report-app.actions';

/**
 * Report Apps — NGXS state slice owning the report-app shell lifecycle.
 * Mirrors {@code FilterAppState}'s descriptor lifecycle (bootstrap → refresh →
 * rebind) but swaps search/matrix-reload for {@link RunReport} (which stashes
 * the {@code {header,columns,rows}} result the renderer consumes).
 *
 * <p>The descriptor IS the contract — every show/hide / editability decision
 * happens server-side. This state caches the latest descriptor + the latest
 * run result + a thin UI scratchpad ({@code filterPanelOpen}).
 *
 * <p>Named {@code reportApp} (NOT {@code report}) to avoid colliding with the
 * unrelated Trovata {@code ReportsState}. See impl doc
 * {@code documentation/implementation/system/tms-report-app.md}.
 */

export class ReportAppStateModel {
	appName!: string | null;
	formId!: string | null;
	descriptor!: ReportAppDescriptor | null;
	result!: ReportResult | null;
	filterPanelOpen!: boolean;
	loading!: boolean;
	error!: string | null;
	restoreInFlight!: boolean;
	profileCd!: string | null;
	// Profile baseline — filter selections captured when the active profile
	// loaded (BootstrapReportApp); `isProfileModified` compares live selections
	// against it to drive the profile-chip "modified" dot.
	baselineSelections!: Readonly<Record<string, FilterSelection>>;
}

const DEFAULT_REPORT_APP_STATE: ReportAppStateModel = {
	appName: null,
	formId: null,
	descriptor: null,
	result: null,
	filterPanelOpen: false,
	loading: false,
	error: null,
	restoreInFlight: false,
	profileCd: null,
	baselineSelections: {},
};

@State<ReportAppStateModel>({
	name: 'reportApp',
	defaults: DEFAULT_REPORT_APP_STATE,
})
@Injectable()
export class ReportAppState {
	@Selector() static appName(state: ReportAppStateModel): string | null {
		return state.appName;
	}
	@Selector() static formId(state: ReportAppStateModel): string | null {
		return state.formId;
	}
	@Selector() static descriptor(state: ReportAppStateModel): ReportAppDescriptor | null {
		return state.descriptor;
	}
	@Selector() static result(state: ReportAppStateModel): ReportResult | null {
		return state.result;
	}
	@Selector() static filterPanelOpen(state: ReportAppStateModel): boolean {
		return state.filterPanelOpen;
	}
	@Selector() static loading(state: ReportAppStateModel): boolean {
		return state.loading;
	}
	@Selector() static error(state: ReportAppStateModel): string | null {
		return state.error;
	}
	@Selector() static restoreInFlight(state: ReportAppStateModel): boolean {
		return state.restoreInFlight;
	}
	@Selector() static profileCd(state: ReportAppStateModel): string | null {
		return state.profileCd;
	}

	/**
	 * Count of active filters — value-set (SSoT): routes through the same
	 * projection + predicate the chips use, so the funnel badge == chip count
	 * (empty rows don't count; range pairs fold to one). Matches FilterAppState.
	 */
	@Selector() static activeFilterCount(state: ReportAppStateModel | undefined): number {
		const descriptor: ReportAppDescriptor | null = state?.descriptor ?? null;
		if (!descriptor) return 0;
		return computeActiveFilterCount(projectFilterSelections(Object.values(descriptor.fields)));
	}

	/** The loaded profile's saved selections — the baseline the "modified" comparisons run against. */
	@Selector() static baselineSelections(state: ReportAppStateModel): Readonly<Record<string, FilterSelection>> {
		return state.baselineSelections ?? {};
	}
	/** True when live filter selections differ from the loaded profile's baseline. */
	@Selector() static isProfileModified(state: ReportAppStateModel): boolean {
		const current: Readonly<Record<string, FilterSelection>> =
			projectFilterSelections(Object.values(state.descriptor?.fields ?? {}));
		return selectionsModified(current, state.baselineSelections ?? {});
	}

	constructor(
		private readonly api: ReportAppApiService,
		private readonly notify: TmsNotificationService,
		private readonly labels: TmsLabelService,
		private readonly formService: TsmAppFormService,
		private readonly downloads: ReportDownloadService
	) {}

	// -- Init / clear --

	@Action(InitReportAppState)
	initReportAppState(ctx: StateContext<ReportAppStateModel>, action: InitReportAppState): void {
		ctx.patchState({
			appName: action.appName,
			profileCd: action.profileCd ?? null,
			error: null,
			// Release (null — do NOT close) any prior app's formId so the upcoming
			// BootstrapReportApp doesn't evict a form Path B has snapshotted for
			// return-nav. Init runs only on cross-app nav / first mount; same-app
			// re-bootstrap (profile switch / Clear) keeps formId and closes the
			// replaced form. Mirrors the REF/Filter fix — prevents the false
			// "session expired" on report→report→back. See ref-app.state.ts.
			formId: null,
		});
	}

	@Action(ClearReportAppState)
	clearReportAppState(ctx: StateContext<ReportAppStateModel>): void {
		ctx.setState(DEFAULT_REPORT_APP_STATE);
	}

	// -- Lifecycle handlers (FORM_EXPIRED detection + restore via shared SSoT) --

	@Action(BootstrapReportApp)
	async bootstrapReportApp(
		ctx: StateContext<ReportAppStateModel>,
		action: BootstrapReportApp,
	): Promise<void> {
		ctx.patchState({ loading: true, error: null });
		// Close any previous form before bootstrapping (profile switch, Clear).
		// Cross-app nav releases formId in InitReportAppState, so this only closes
		// same-app forms (no leak). Shared SSoT helper; non-fatal if it fails.
		await closeFormQuietly(this.formService, ctx.getState().formId, '[BootstrapReportApp]');
		try {
			const idempotencyKey: string = crypto.randomUUID();
			const descriptor: ReportAppDescriptor = await firstValueFrom(
				this.api.bootstrap(action.appName, action.profileCd, idempotencyKey),
			);
			ctx.patchState({
				appName: action.appName,
				profileCd: action.profileCd ?? null,
				// Snapshot the saved-profile baseline for the "modified" dot. Reset on
				// every (re-)bootstrap — profile switch AND the Reset button (which
				// re-bootstraps), so after Reset the dot clears.
				baselineSelections: projectFilterSelections(Object.values(descriptor.fields ?? {})),
				// A bootstrap is a fresh start (initial load OR a Clear re-bootstrap):
				// drop any previously-rendered report so the displayed output is
				// cleared. Mirrors legacy refreshReportResults() which re-defaults the
				// form AND reverts the listing to "No Results to Display".
				result: null,
			});
			// Awaited (was a bare dispatch): the applier is what patches formId, and the
			// auto-run below throws without one. NGXS dispatch happens to be eager for a
			// synchronous handler, but relying on that is a hidden coupling.
			await firstValueFrom(ctx.dispatch(new ApplyReportDescriptorUpdate(descriptor)));
			ctx.dispatch(new LoadReportAppProfiles(action.appName));
			// Opening or switching a saved view RUNS its query (§18) — the report no
			// longer waits for a Search click. Dispatched here rather than from the page
			// so every "the view changed" path inherits it (launch, both profile-switch
			// paths, the panel's Reset), and the callers that must NOT run pass
			// autoRun:false.
			//
			// EXCEPT a workbook profile. RunReport hands over the file (that branch lives
			// there, so a manual Search / toolbar Search / panel Apply all download), but a
			// mere NAVIGATION must not put a file in someone's downloads folder — and it
			// would do so again on every revisit. Optional-chained: a descriptor with no
			// format block falls back to running, never breaks the open.
			if (action.autoRun && !isWorkbookOutput(descriptor)) {
				// AWAITED, inside the try, so `loading` stays true for the whole open:
				// this method's finally clears it, and a fire-and-forget dispatch left
				// the body rendering "Run the report to see results." for the entire
				// engine pass — the very message this change exists to remove.
				try {
					await firstValueFrom(ctx.dispatch(new RunReport()));
				} catch (err: unknown) {
					// Deliberately not rethrown: RunReport already patched `error` into
					// this same slice, and failing the BOOTSTRAP would make the Path-B
					// restore hook read "no form" and answer with a "reopen fresh" toast.
					console.warn('[BootstrapReportApp] auto-run failed (error already surfaced)', err);
				}
			}
		} catch (err: unknown) {
			ctx.patchState({ error: errorMessage(err) });
			throw err;
		} finally {
			ctx.patchState({ loading: false });
		}
	}

	@Action(LoadReportAppProfiles)
	async loadReportAppProfiles(
		ctx: StateContext<ReportAppStateModel>,
		action: LoadReportAppProfiles,
	): Promise<void> {
		try {
			const res: ProfilesApiResponse = await firstValueFrom(this.api.getProfiles(action.appName));
			const status: string = (res?.status ?? '').toLowerCase();
			if (!res || status !== 'success' || !Array.isArray(res.profiles)) {
				// 200-wrapped server failure — surface it (a silently-empty
				// dropdown reads as "no saved views" and feeds Update's
				// row-scope fallback).
				ctx.patchState({ error: 'Failed to load profiles.' });
				return;
			}
			const state: ReportAppStateModel = ctx.getState();
			const descriptor: ReportAppDescriptor | null = state.descriptor;
			if (!descriptor) return;
			// Shared mapper — capability flags ride along for the Manage dialog
			// (one field list for both filter AND report loaders).
			const available: ProfileOption[] = profilesToProfileOptions(res.profiles);
			ctx.patchState({
				descriptor: {
					...descriptor,
					profile: { ...descriptor.profile, available },
				},
			});
		} catch (err: unknown) {
			ctx.patchState({ error: errorMessage(err) });
		}
	}

	@Action(RefreshReportApp)
	async refreshReportApp(
		ctx: StateContext<ReportAppStateModel>,
		action: RefreshReportApp,
	): Promise<void> {
		const state: ReportAppStateModel = ctx.getState();
		if (!state.formId) {
			throw new Error('RefreshReportApp dispatched with no formId — BootstrapReportApp must run first');
		}
		try {
			const idempotencyKey: string = crypto.randomUUID();
			const descriptor: ReportAppDescriptor = await firstValueFrom(
				this.api.refresh(state.formId, action.changedFields, idempotencyKey),
			);
			ctx.dispatch(new ApplyReportDescriptorUpdate(descriptor));
		} catch (err: unknown) {
			if (!isFormExpiredError(err)) {
				ctx.patchState({ error: errorMessage(err) });
				throw err;
			}
			if (!state.appName) {
				throw new Error('FORM_EXPIRED retry requires state.appName; missing');
			}
			await retryOnFormExpired(err, {
				reBootstrap: () => firstValueFrom(ctx.dispatch(new BootstrapReportApp(state.appName!, state.profileCd ?? undefined, false))),
				// autoRun:false — `replay` below re-runs the original action, so letting the
				// re-bootstrap auto-run too would run the report twice.
				replay: () => firstValueFrom(ctx.dispatch(new RefreshReportApp(action.changedFields))),
			});
		}
	}

	@Action(RunReport)
	async runReport(ctx: StateContext<ReportAppStateModel>): Promise<void> {
		const state: ReportAppStateModel = ctx.getState();
		if (!state.formId) {
			throw new Error('RunReport dispatched with no formId — BootstrapReportApp must run first');
		}
		if (!state.appName) {
			throw new Error('RunReport dispatched with no appName — BootstrapReportApp must run first');
		}
		ctx.patchState({ loading: true });
		try {
			const idempotencyKey: string = crypto.randomUUID();
			const result: ReportResult = await firstValueFrom(
				this.api.runReport(state.appName, state.formId, undefined, idempotencyKey),
			);
			ctx.dispatch(new ApplyReportResult(result));
			// A report's OUTPUT is what its profile says it is: for format=excel that is a
			// workbook, so producing it means handing over the file. This lives HERE, not in the
			// page, because every caller must get it — the magnifier, the toolbar's Search, the
			// filter panel's Apply (which silently skipped the download while the page owned this
			// branch) and the open/switch auto-run. The rows are still stashed above, so the
			// screen shows the report as well as saving it.
			await this.exportIfWorkbook(ctx, state.appName);
		} catch (err: unknown) {
			// Form-expired (410) recovery — the cached server processor for this
			// formId is gone (server cycle / session timeout while the client still
			// holds a stale formId, e.g. after a hard refresh). Re-bootstrap to mint
			// a fresh processor + formId, then re-run (shared SSoT retry). appName is
			// guaranteed non-null by the guard above.
			if (!isFormExpiredError(err)) {
				ctx.patchState({ error: errorMessage(err) });
				throw err;
			}
			await retryOnFormExpired(err, {
				reBootstrap: () => firstValueFrom(ctx.dispatch(new BootstrapReportApp(state.appName!, state.profileCd ?? undefined, false))),
				// autoRun:false — `replay` below re-runs the original action, so letting the
				// re-bootstrap auto-run too would run the report twice.
				replay: () => firstValueFrom(ctx.dispatch(new RunReport())),
			});
		} finally {
			ctx.patchState({ loading: false });
		}
	}

	/**
	 * Hand over the workbook when the active profile's output is excel. Failure is reported and
	 * swallowed: the engine pass DID succeed and its rows are already on screen, so a failed file
	 * fetch must not present the whole run as failed.
	 */
	private async exportIfWorkbook(ctx: StateContext<ReportAppStateModel>, appName: string): Promise<void> {
		const state: ReportAppStateModel = ctx.getState();
		if (!isWorkbookOutput(state.descriptor) || !state.formId) {
			return;
		}
		try {
			const blob: Blob = await firstValueFrom(this.downloads.fetchFile(appName, state.formId, 'XLSX'));
			this.downloads.triggerDownload(blob, this.downloads.exportFilename(appName, state.result?.referenceId, 'xlsx'));
		} catch (err: unknown) {
			console.warn('[RunReport] workbook export failed after a successful run', err);
			this.notify.error(this.labels.resolve('excel_export_failed_msg', 'Excel export failed.'));
		}
	}

	@Action(RebindReportForm)
	async rebindReportForm(
		ctx: StateContext<ReportAppStateModel>,
		action: RebindReportForm,
	): Promise<void> {
		const idempotencyKey: string = crypto.randomUUID();
		const descriptor: ReportAppDescriptor = await firstValueFrom(
			this.api.rebind(action.formId, idempotencyKey),
		);
		ctx.patchState({ profileCd: action.profileCd });
		ctx.dispatch(new ApplyReportDescriptorUpdate(descriptor));
	}

	// -- Internal appliers --

	@Action(ApplyReportDescriptorUpdate)
	applyReportDescriptorUpdate(
		ctx: StateContext<ReportAppStateModel>,
		action: ApplyReportDescriptorUpdate,
	): void {
		const state: ReportAppStateModel = ctx.getState();
		// Server descriptors carry an EMPTY profile.available — keep the loaded
		// saved-views list across same-app refresh/rebind (it was wiped on every
		// filter-chip change otherwise).
		const descriptor: ReportAppDescriptor = carryProfileOptionsForward(state.descriptor, action.descriptor);
		const nextFormId: string = descriptor.formId || state.formId || '';
		ctx.patchState({
			descriptor,
			formId: nextFormId || state.formId,
		});
	}

	@Action(ApplyReportResult)
	applyReportResult(ctx: StateContext<ReportAppStateModel>, action: ApplyReportResult): void {
		ctx.patchState({
			result: action.result,
			formId: action.result.formId || ctx.getState().formId,
		});
	}

	@Action(SetReportFormat)
	setReportFormat(ctx: StateContext<ReportAppStateModel>, action: SetReportFormat): void {
		const descriptor: ReportAppDescriptor | null = ctx.getState().descriptor;
		if (!descriptor) return;
		ctx.patchState({
			descriptor: {
				...descriptor,
				format: { ...descriptor.format, current: action.format },
			},
		});
	}

	@Action(ToggleReportFilterPanel)
	toggleReportFilterPanel(ctx: StateContext<ReportAppStateModel>, action: ToggleReportFilterPanel): void {
		ctx.patchState({ filterPanelOpen: action.open });
	}

	// -- Path B mini-orchestrator --

	@Action(RestoreReportAppSnapshot)
	async restoreReportAppSnapshot(
		ctx: StateContext<ReportAppStateModel>,
		{ snapshot }: RestoreReportAppSnapshot,
	): Promise<void> {
		// Path B form recovery via the shared SSoT orchestrator.
		await restoreFormSnapshot({
			hasFormId: !!snapshot.formId,
			isRestoreInFlight: () => ctx.getState().restoreInFlight,
			setRestoreInFlight: (v: boolean) => ctx.patchState({ restoreInFlight: v }),
			// Report is a read-only runner (no edit mode), so a rebind 410 never loses
			// user edits — reopen fresh silently, never the "unsaved changes gone" toast.
			editAtRisk: false,
			rebind: () => firstValueFrom(ctx.dispatch(new RebindReportForm(
				snapshot.formId!,
				snapshot.profileCd,
			))),
			bootstrap: () => firstValueFrom(ctx.dispatch(new BootstrapReportApp(
				snapshot.appName,
				snapshot.profileCd ?? undefined,
			))),
			// Report has a single result table (no coordinator); restore the only
			// UI scratchpad slice the server doesn't own.
			onRestored: () => ctx.patchState({ filterPanelOpen: snapshot.filterPanelOpen }),
			notify: this.notify,
			labels: this.labels,
			logTag: '[RestoreReportAppSnapshot]',
		});
	}
}

function errorMessage(err: unknown): string {
	if (err instanceof HttpErrorResponse) {
		return (err.error as { message?: string } | null)?.message
			?? err.message
			?? 'Server error';
	}
	if (err instanceof Error) return err.message;
	return 'Unknown error';
}
